import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, CheckCircle, Edit3, Printer, RotateCcw, Save, Trash2, XCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import { api } from '@/services/api';
import { Booking, UpdateBookingDetailsForm } from '@/types';
import { formatNZD } from '@/utils/currency';
import Modal from '@/components/ui/Modal';
import { getBookingEffectiveStatus, hasBookingEndedNz } from '@/utils/bookingStatus';

type BookingFormState = {
  customer_name: string;
  customer_phone: string;
  customer_email: string;
  player_count: number;
  special_requests: string;
};

const BookingDetail: React.FC = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [booking, setBooking] = useState<Booking | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editMode, setEditMode] = useState(searchParams.get('mode') === 'edit');
  const backUrl = searchParams.get('from_date') ? `/bookings?date=${searchParams.get('from_date')}` : '/bookings';
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelError, setCancelError] = useState<string | null>(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [form, setForm] = useState<BookingFormState>({
    customer_name: '',
    customer_phone: '',
    customer_email: '',
    player_count: 1,
    special_requests: '',
  });

  const refreshBooking = async (bookingId: number) => {
    const refreshed = await api.bookings.getById(bookingId);
    if (!refreshed.data) return;
    const normalized = {
      ...(refreshed.data as Booking),
      status: getBookingEffectiveStatus(refreshed.data as Booking),
    };
    setBooking(normalized);
  };

  useEffect(() => {
    const fetchBooking = async () => {
      if (!id) return;
      setLoading(true);
      const result = await api.bookings.getById(Number(id));
      if (result.error || !result.data) {
        toast.error(t('errors.serverError'));
        setLoading(false);
        return;
      }
      const data = result.data as Booking;
      const normalized = { ...data, status: getBookingEffectiveStatus(data) };
      setBooking(normalized);
      const customerNameFallback =
        normalized.customer_name ||
        normalized.customer_display_name ||
        normalized.customer_email ||
        normalized.customer_email_reg ||
        '';
      setForm({
        customer_name: customerNameFallback,
        customer_phone: normalized.customer_phone || normalized.customer_phone_reg || '',
        customer_email: normalized.customer_email || normalized.customer_email_reg || '',
        player_count: normalized.player_count || 1,
        special_requests: normalized.special_requests || '',
      });
      setLoading(false);
    };
    fetchBooking();
  }, [id, t]);

  const hasChanges = useMemo(() => {
    if (!booking) return false;
    return (
      form.customer_name !== (booking.customer_name || '') ||
      form.customer_phone !== (booking.customer_phone || booking.customer_phone_reg || '') ||
      form.customer_email !== (booking.customer_email || booking.customer_email_reg || '') ||
      form.player_count !== (booking.player_count || 1) ||
      form.special_requests !== (booking.special_requests || '')
    );
  }, [booking, form]);

  const handleSave = async () => {
    if (!booking || !hasChanges) return;
    setSaving(true);
    const updates: UpdateBookingDetailsForm = {};
    if (form.customer_name !== (booking.customer_name || '')) updates.customer_name = form.customer_name;
    if (form.customer_phone !== (booking.customer_phone || booking.customer_phone_reg || '')) updates.customer_phone = form.customer_phone;
    if (form.customer_email !== (booking.customer_email || booking.customer_email_reg || '')) updates.customer_email = form.customer_email;
    if (form.player_count !== (booking.player_count || 1)) updates.player_count = form.player_count;
    if (form.special_requests !== (booking.special_requests || '')) updates.special_requests = form.special_requests;

    try {
      const toastId = toast.loading(t('common.saving'));
      const result = await api.bookings.updateDetails(booking.id, updates);
      toast.dismiss(toastId);
      if (result.error) {
        toast.error(t('common.operationFailed'));
        return;
      }
      toast.success(t('common.saved'));
      setEditMode(false);
      await refreshBooking(booking.id);
    } catch (error) {
      toast.dismiss();
      toast.error(t('common.operationFailed'));
    } finally {
      setSaving(false);
    }
  };

  const getStatusLabel = (status: Booking['status']) => {
    switch (status) {
      case 'confirmed':
        return t('bookings.confirmed');
      case 'pending':
        return t('bookings.pending');
      case 'cancelled':
        return t('bookings.cancelled');
      case 'completed':
        return t('bookings.completed');
      case 'expired':
        return t('bookings.expired');
      default:
        return status;
    }
  };

  const handleCancel = async () => {
    if (!booking) return;
    const reason = cancelReason.trim();
    if (!reason) {
      setCancelError(t('booking.cancelReasonRequired'));
      return;
    }

    const toastId = toast.loading(t('booking.cancelling'));
    const result = await api.bookings.cancel(booking.id, reason);
    toast.dismiss(toastId);

    if (result.error) {
      if (result.status === 404) {
        toast.error(t('errors.resourceNotFound'));
        navigate(backUrl);
        return;
      }
      toast.error(t('booking.cancelFailed'));
      return;
    }

    toast.success(t('booking.cancelled', { id: booking.id }));
    setCancelOpen(false);
    setCancelReason('');
    setCancelError(null);
    await refreshBooking(booking.id);
  };

  const handleConfirm = async () => {
    if (!booking) return;
    const toastId = toast.loading(t('booking.confirming'));
    const result = await api.bookings.updateStatus(booking.id, {
      status: 'confirmed',
      notes: t('booking.staffConfirmedBooking')
    });
    toast.dismiss(toastId);

    if (result.error) {
      if (result.status === 404) {
        toast.error(t('errors.resourceNotFound'));
        navigate(backUrl);
        return;
      }
      toast.error(t('common.operationFailed'));
      return;
    }

    toast.success(t('booking.confirmed', { id: booking.id }));
    await refreshBooking(booking.id);
  };

  const handlePrintReceipt = async () => {
    if (!booking) return;

    try {
      const toastId = toast.loading(t('receipt.generating'));
      const printWindow = window.open('', '_blank', 'width=420,height=600');
      if (!printWindow) {
        toast.dismiss(toastId);
        toast.error(t('receipt.popupBlocked'));
        return;
      }

      const printedAt = new Date().toLocaleString('en-NZ', { timeZone: 'Pacific/Auckland' });
      const displayCustomerName =
        booking.customer_name || booking.customer_display_name || booking.customer_email || booking.customer_email_reg || '—';
      const displayCustomerContact =
        booking.customer_phone || booking.customer_phone_reg || booking.customer_email || booking.customer_email_reg || '—';

      const html = `<!doctype html>
<html>
  <head>
    <meta charset="utf-8" />
    <title>Receipt #${booking.id}</title>
    <style>
      body { font-family: ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Arial, sans-serif; padding: 16px; }
      h1 { font-size: 18px; margin: 0 0 4px; }
      .muted { color: #6b7280; font-size: 12px; }
      .box { border: 1px solid #e5e7eb; border-radius: 12px; padding: 12px; margin-top: 12px; }
      .row { display: flex; justify-content: space-between; gap: 12px; margin: 8px 0; font-size: 13px; }
      .label { color: #6b7280; }
      .value { color: #111827; text-align: right; }
      .total { font-size: 16px; font-weight: 700; }
      hr { border: 0; border-top: 1px dashed #e5e7eb; margin: 12px 0; }
      @media print { .no-print { display: none; } body { padding: 0; } }
    </style>
  </head>
  <body>
    <h1>Nexcave Receipt</h1>
    <div class="muted">Printed at ${printedAt} (NZ)</div>

    <div class="box">
      <div class="row"><div class="label">Booking</div><div class="value">#${booking.id.toString().padStart(6, '0')}</div></div>
      <div class="row"><div class="label">Venue</div><div class="value">${booking.location_name}</div></div>
      <div class="row"><div class="label">Court</div><div class="value">${booking.court_name}</div></div>
      <div class="row"><div class="label">Date</div><div class="value">${booking.date}</div></div>
      <div class="row"><div class="label">Time</div><div class="value">${booking.start_time} - ${booking.end_time}</div></div>
      <hr />
      <div class="row"><div class="label">Customer</div><div class="value">${displayCustomerName}</div></div>
      <div class="row"><div class="label">Contact</div><div class="value">${displayCustomerContact}</div></div>
      <div class="row"><div class="label">Players</div><div class="value">${booking.player_count}</div></div>
      <hr />
      <div class="row total"><div class="label">Total</div><div class="value">${formatNZD(booking.total_price)}</div></div>
      <div class="row"><div class="label">Payment</div><div class="value">${booking.payment_method} / ${booking.payment_status}</div></div>
      <div class="row"><div class="label">Status</div><div class="value">${booking.status}</div></div>
    </div>

    <div class="no-print muted" style="margin-top: 12px;">Close this tab after printing.</div>
    <script>window.focus(); window.print();</script>
  </body>
</html>`;

      printWindow.document.open();
      printWindow.document.write(html);
      printWindow.document.close();

      toast.dismiss(toastId);
      toast.success(t('receipt.sentToPrinter'));
    } catch (error) {
      toast.dismiss();
      toast.error(t('receipt.printFailed'));
    }
  };

  const handleComplete = async () => {
    if (!booking) return;
    const toastId = toast.loading(t('booking.completing'));
    const result = await api.bookings.complete(booking.id);
    toast.dismiss(toastId);

    if (result.error) {
      if (result.status === 404) {
        toast.error(t('errors.resourceNotFound'));
        navigate(backUrl);
        return;
      }
      toast.error(t('common.operationFailed'));
      return;
    }

    toast.success(t('booking.completed', { id: booking.id }));
    await refreshBooking(booking.id);
  };

  const handleRestore = async () => {
    if (!booking) return;
    const toastId = toast.loading(t('booking.restoring'));
    const result = await api.bookings.restore(booking.id);
    toast.dismiss(toastId);

    if (result.error) {
      if (result.status === 404) {
        toast.error(t('errors.resourceNotFound'));
        navigate(backUrl);
        return;
      }
      toast.error(t('booking.restoreFailed'));
      return;
    }

    toast.success(t('booking.restored', { id: booking.id }));
    await refreshBooking(booking.id);
  };

  const handleDelete = async () => {
    if (!booking) return;
    const toastId = toast.loading(t('booking.deleting'));
    const result = await api.bookings.delete(booking.id);
    toast.dismiss(toastId);

    if (result.error) {
      if (result.status === 404) {
        toast.error(t('errors.resourceNotFound'));
        navigate(backUrl);
        return;
      }
      toast.error(t('booking.deleteFailed'));
      return;
    }

    toast.success(t('booking.deleted', { id: booking.id }));
    setDeleteOpen(false);
    navigate(backUrl);
  };

  if (loading) {
    return (
      <div className="card card-comfortable">
        <div className="text-sm text-gray-500">{t('common.loading')}</div>
      </div>
    );
  }

  if (!booking) {
    return (
      <div className="card card-comfortable">
        <div className="text-sm text-red-600">{t('errors.resourceNotFound')}</div>
      </div>
    );
  }

  const isPast = hasBookingEndedNz(booking);
  const canConfirm = booking.status === 'pending';
  const canCancel = booking.status !== 'cancelled' && booking.status !== 'completed';
  const canComplete =
    isPast && (booking.status === 'confirmed' || booking.status === 'pending' || booking.status === 'expired');
  const canRestore = booking.status === 'cancelled';
  const canDelete = booking.status === 'cancelled';
  const canPrint = booking.status !== 'cancelled';
  const displayCustomerName =
    form.customer_name || booking.customer_name || booking.customer_display_name || booking.customer_email || booking.customer_email_reg || '—';
  const displayCustomerPhone =
    form.customer_phone || booking.customer_phone || booking.customer_phone_reg || '—';
  const displayCustomerEmail =
    form.customer_email || booking.customer_email || booking.customer_email_reg || '—';

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <button
          onClick={() => navigate(backUrl)}
          className="btn btn-secondary gap-2"
        >
          <ArrowLeft className="h-4 w-4" />
          {t('common.back')}
        </button>
        <div className="flex items-center gap-2">
          {canConfirm && (
            <button onClick={handleConfirm} className="btn btn-primary gap-2">
              <CheckCircle className="h-4 w-4" />
              {`${t('bookings.confirmBooking')} / Confirm Booking`}
            </button>
          )}
          {canRestore && (
            <button onClick={handleRestore} className="btn btn-outline gap-2">
              <RotateCcw className="h-4 w-4" />
              {t('bookings.restoreBooking')}
            </button>
          )}
          {canComplete && (
            <button onClick={handleComplete} className="btn btn-primary gap-2">
              <CheckCircle className="h-4 w-4" />
              {t('bookings.completeBooking')}
            </button>
          )}
          {canCancel && (
            <button
              onClick={() => {
                setCancelOpen(true);
                setCancelReason('');
                setCancelError(null);
              }}
              className="btn btn-danger gap-2"
            >
              <XCircle className="h-4 w-4" />
              {`${t('bookings.cancelBooking')} / Cancel Booking`}
            </button>
          )}
          {canDelete && (
            <button onClick={() => setDeleteOpen(true)} className="btn btn-danger gap-2">
              <Trash2 className="h-4 w-4" />
              {t('bookings.deleteBooking')}
            </button>
          )}
          {canPrint && (
            <button onClick={handlePrintReceipt} className="btn btn-secondary gap-2">
              <Printer className="h-4 w-4" />
              {`${t('bookings.printReceipt')} / Print`}
            </button>
          )}
          <button
            onClick={() => setEditMode((prev) => !prev)}
            className="btn btn-outline gap-2"
          >
            <Edit3 className="h-4 w-4" />
            {editMode ? t('common.view') : t('common.edit')}
          </button>
          <button
            onClick={handleSave}
            disabled={!hasChanges || saving}
            className="btn btn-primary gap-2"
          >
            <Save className="h-4 w-4" />
            {t('common.save')}
          </button>
        </div>
      </div>

      <div className="card card-comfortable">
        <div className="space-y-4">
          <div className="flex items-center justify-between gap-4">
            <div className="text-lg font-semibold text-gray-900">
              #{booking.id.toString().padStart(6, '0')} · {booking.court_name}
            </div>
            <span className="px-3 py-1 rounded-full text-xs font-medium border border-gray-200 bg-gray-50 text-gray-700">
              {getStatusLabel(booking.status)}
            </span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-gray-700">
            <div>
              <div className="text-gray-500">{t('createBooking.date')}</div>
              <div>{booking.date}</div>
            </div>
            <div>
              <div className="text-gray-500">{t('createBooking.time')}</div>
              <div>{booking.start_time} - {booking.end_time}</div>
            </div>
            <div>
              <div className="text-gray-500">{t('bookings.totalAmount')}</div>
              <div className="font-medium">{formatNZD(booking.total_price)}</div>
            </div>
          </div>
        </div>
      </div>

      <div className="card card-comfortable">
        <div className="space-y-4">
          <div className="text-base font-semibold text-gray-900">{t('createBooking.customerInfo')}</div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-gray-700">
            <div>
              <div className="text-gray-500">{t('createBooking.customerName')}</div>
              <div className="font-medium break-all">{displayCustomerName}</div>
            </div>
            <div>
              <div className="text-gray-500">{t('createBooking.phone')}</div>
              <div className="break-all">{displayCustomerPhone}</div>
            </div>
            <div>
              <div className="text-gray-500">{t('createBooking.email')}</div>
              <div className="break-all">{displayCustomerEmail}</div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-gray-500 mb-1">{t('createBooking.customerName')}</label>
              <input
                className="input w-full"
                value={form.customer_name}
                onChange={(e) => setForm({ ...form, customer_name: e.target.value })}
                disabled={!editMode}
              />
            </div>
            <div>
              <label className="block text-sm text-gray-500 mb-1">{t('createBooking.phone')}</label>
              <input
                className="input w-full"
                value={form.customer_phone}
                onChange={(e) => setForm({ ...form, customer_phone: e.target.value })}
                disabled={!editMode}
              />
            </div>
            <div>
              <label className="block text-sm text-gray-500 mb-1">{t('createBooking.email')}</label>
              <input
                className="input w-full"
                value={form.customer_email}
                onChange={(e) => setForm({ ...form, customer_email: e.target.value })}
                disabled={!editMode}
              />
            </div>
            <div>
              <label className="block text-sm text-gray-500 mb-1">{t('createBooking.playerCount')}</label>
              <input
                type="number"
                min={1}
                max={20}
                className="input w-full"
                value={form.player_count}
                onChange={(e) => setForm({ ...form, player_count: Number(e.target.value) || 1 })}
                disabled={!editMode}
              />
            </div>
          </div>

          <div>
            <label className="block text-sm text-gray-500 mb-1">{t('createBooking.specialRequests')}</label>
            <textarea
              className="input w-full min-h-[120px]"
              value={form.special_requests}
              onChange={(e) => setForm({ ...form, special_requests: e.target.value })}
              disabled={!editMode}
            />
          </div>
        </div>
      </div>

      <Modal
        isOpen={cancelOpen}
        onClose={() => setCancelOpen(false)}
        title={t('bookings.cancelBooking')}
        size="md"
      >
        <div className="space-y-4">
          <div className="text-sm text-gray-600">
            {`#${booking.id.toString().padStart(6, '0')} · ${booking.court_name}`}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('bookings.cancellationReason')} <span className="text-red-500">*</span>
            </label>
            <textarea
              className={`w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary ${cancelError ? 'border-red-500' : 'border-gray-300'
                }`}
              rows={3}
              value={cancelReason}
              onChange={(e) => {
                setCancelReason(e.target.value);
                if (cancelError) setCancelError(null);
              }}
              placeholder={t('booking.cancelReasonPlaceholder')}
            />
            {cancelError && <p className="mt-2 text-sm text-red-600">{cancelError}</p>}
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button className="btn btn-secondary" onClick={() => setCancelOpen(false)}>
              {t('common.cancel')}
            </button>
            <button className="btn btn-danger" onClick={handleCancel}>
              {t('common.confirm')}
            </button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        title={t('bookings.deleteBooking')}
        size="md"
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-700">{t('booking.deleteConfirm')}</p>
          <div className="text-sm text-gray-600">
            {`#${booking.id.toString().padStart(6, '0')} · ${booking.court_name}`}
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button className="btn btn-secondary" onClick={() => setDeleteOpen(false)}>
              {t('common.cancel')}
            </button>
            <button className="btn btn-danger" onClick={handleDelete}>
              {t('common.delete')}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default BookingDetail;
